import { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, userIsClient } from '@/store/auth';

interface Props {
  children: ReactNode;
  role?: 'agency' | 'client';
}

export function AuthGuard({ children, role }: Props) {
  const { user, token, refresh } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (token && !user) {
      refresh().catch(() => {});
    }
  }, [token, user, refresh]);

  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Client users may only see /client/*; bounce them out of the agency
  // shell so /accounts, /reports, /copilot etc. don't leak data.
  if (role === 'agency' && user && userIsClient(user)) {
    return <Navigate to="/client" replace />;
  }

  // Agency users (or users without a `client` role) cannot enter the
  // white-label client portal — they are sent to the agency dashboard.
  if (role === 'client' && user && !userIsClient(user)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
